interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
}

export const setPageMeta = (title: string, description: string, config?: Partial<SEOConfig>) => {
  // Update title
  document.title = title;

  // Helper to set or create meta tag
  const setMeta = (name: string, content: string, isProperty = false) => {
    const attr = isProperty ? 'property' : 'name';
    let meta = document.querySelector(`meta[${attr}="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(attr, name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  // Primary meta tags
  setMeta('description', description);
  setMeta('title', title);

  // Open Graph
  setMeta('og:title', title, true);
  setMeta('og:description', description, true);

  // Twitter
  setMeta('twitter:title', title);
  setMeta('twitter:description', description);

  // Optional config
  if (config?.keywords) {
    setMeta('keywords', config.keywords);
  }

  if (config?.ogImage) {
    setMeta('og:image', config.ogImage, true);
    setMeta('twitter:image', config.ogImage);
  }

  if (config?.canonical) {
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', config.canonical);
  }
};

// Page-specific SEO configs
export const SEO_CONFIGS = {
  home: {
    title: 'HappySpace — The AI Operating System for Your Business',
    description: 'One AI to replace your web developer, marketing agency, and a dozen software subscriptions. Tell Happy what you need—websites, ads, reviews, operations—and it makes it happen. Free during beta.',
    keywords: 'AI operating system, AI website builder, AI marketing, restaurant website, ecommerce website, small business website, online ordering, marketing automation'
  },
  about: {
    title: 'About HappySpace — Meet the Team Behind Your AI Marketing Partner',
    description: 'HappySpace is on a mission to give every small business access to enterprise-level marketing. Learn about our team and why we\'re building the future of AI marketing.',
    keywords: 'HappySpace team, AI startup, small business marketing, about HappySpace'
  },
  restaurants: {
    title: 'AI Website Builder for Restaurants — Online Ordering in 60 Seconds',
    description: 'Build a restaurant website with online ordering, menu management, and delivery integration. Happy sets up everything—you just take orders. Free during beta.',
    keywords: 'restaurant website builder, online ordering system, restaurant menu, food delivery website, AI restaurant marketing'
  },
  ecommerce: {
    title: 'AI Website Builder for E-commerce — Start Selling in Minutes',
    description: 'Launch your online store with AI. Products, payments, shipping—all set up for you. Happy handles the tech so you can focus on your business.',
    keywords: 'ecommerce website builder, AI online store, product catalog, payment integration, small business ecommerce'
  },
  services: {
    title: 'AI Website Builder for Service Businesses — Booking in 60 Seconds',
    description: 'Build a professional website for your salon, spa, fitness studio, or consulting business. Online booking, payments, and AI customer chat—all set up for you.',
    keywords: 'service business website, salon website builder, spa booking system, fitness studio website, contractor website, AI booking system'
  },
  pricing: {
    title: 'HappySpace Pricing — Free During Beta',
    description: 'Get full access to HappySpace during our beta period. AI website builder, online ordering, customer chat, and more—all free while we\'re in beta.',
    keywords: 'HappySpace pricing, AI marketing cost, website builder pricing, free website builder'
  },
  blog: {
    title: 'HappySpace Blog — AI Marketing Tips for Small Businesses',
    description: 'Learn how to grow your small business with AI-powered marketing. Tips on websites, online ordering, customer engagement, and more.',
    keywords: 'small business marketing tips, AI marketing blog, restaurant marketing, ecommerce tips'
  },
  login: {
    title: 'Sign In — HappySpace',
    description: 'Sign in to your HappySpace account to manage your business, view orders, and chat with customers.'
  },
  register: {
    title: 'Get Started — HappySpace',
    description: 'Create your HappySpace account and let Happy build your website, set up ordering, and start growing your business.'
  }
};
